import { useQuery } from "react-query";
import {
    ActionIcon,
    Button,
    SegmentedControl,
    Title,
    Text,
    LoadingOverlay,
    Group,
    Badge,
    Divider,
    Tooltip,
} from "@mantine/core";
import axios from "axios";
import { useStorage } from "./ZustandState";
import { PlayerColor } from "./models";
import { useNavigate, useParams } from "react-router-dom";
import { useViewportSize } from "@mantine/hooks";
import { IconArrowRight, IconDownload } from "@tabler/icons-react";
import { OtAttempt2 } from "./PositionStats";
import { getAverageEval } from "./statsUtils";
import GeneralStatsPaperBlocks from "./GeneralStatsPaperBlocks";
import { DataTable, DataTableSortStatus } from "mantine-datatable";
import sortBy from "lodash/sortBy";
import { useEffect, useState } from "react";
import _ from "lodash";
import Header from "./Header";
import { getUser, redirectToPremiumIfNotPremium } from "./Authentication";
import { FORCE_PREMIUM } from "./constants";

interface Stats {
    generalStats: GeneralStats;
    positionStats: PositionStat[];
}

export interface PositionStat {
    id: number;
    fen: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    index: number;
    eco: string;
    defaultPlayerColor: string;
    pgn: string;
    rating: string;
    OtAttempt2: OtAttempt2[];
}

export interface GeneralStats {
    totalNumAttempts: number;
    totalWhiteGames: number;
    totalBlackGames: number;
    averageRating: number;
    averageWhiteRating: number;
    averageBlackRating: number;
    totalNumPositions: number;
    numWhitePositions: number;
    numBlackPositions: number;
}

export const fetchStats = async (): Promise<Stats> => {
    const profileData = await axios.get(`${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_stats`, {
        withCredentials: true,
    });

    const data = profileData.data;

    return data;

    // const reversedData = profileData.data.reverse();

    // return reversedData;
};

const Stats = () => {
    const { data: leData, isLoading } = useQuery(["otStats"], () => fetchStats());
    const { data: user, isLoading: isLoadingUser } = useQuery(["user"], () => getUser());

    redirectToPremiumIfNotPremium(user);

    const data = leData?.positionStats;

    const setPlayerColor = useStorage((state) => state.setPlayerColor);
    const playerColor = useStorage((state) => state.playerColor);
    const navigate = useNavigate();
    const { width } = useViewportSize();

    const openingsToDisplay = data?.filter((position) => position.defaultPlayerColor === playerColor);

    const handleOnDownload = async () => {
        const allAttempts = await axios.get<unknown, { data: OtAttempt2[] }>(
            `${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_all_attempts`,
            {
                withCredentials: true,
            },
        );

        const fileName = `all_${playerColor === "w" ? "white" : "black"}_games.pgn`;
        const element = document.createElement("a");
        const attemptsToDownload = allAttempts.data.filter((attempt) => attempt.playerColor === playerColor);
        const pgns = attemptsToDownload.map((attempt) => attempt.pgn).join("\n\n");
        const file = new Blob([pgns], { type: "text/plain" });
        element.href = URL.createObjectURL(file);
        element.download = fileName;
        document.body.appendChild(element);
        element.click();
    };

    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<any>>({
        columnAccessor: "name",
        direction: "asc",
    });
    const [records, setRecords] = useState(openingsToDisplay);

    useEffect(() => {
        if (_.isEmpty(openingsToDisplay)) {
            return;
        }
        const data = sortBy(openingsToDisplay, sortStatus.columnAccessor) as PositionStat[];
        setRecords(sortStatus.direction === "desc" ? data.reverse() : data);
    }, [sortStatus]);

    useEffect(() => {
        if (data) {
            setRecords(data?.filter((position) => position.defaultPlayerColor === playerColor));
        }
    }, [data, playerColor]);

    if (isLoading) {
        return <LoadingOverlay visible />;
    }

    const isMobile = width < 1024;

    return (
        <div className="flex flex-col [@media(min-width:1023px)]:items-center space-y-8 mb-16 w-[100vw]">
            <Header user={user} selected="statistics" />
            {/* <BackButton /> */}
            <div className="flex items-center justify-center space-x-2">
                <Title>Statistics</Title>
                <Tooltip label="Download games">
                    <ActionIcon onClick={handleOnDownload} aria-label="Download games" variant="transparent">
                        <IconDownload />
                    </ActionIcon>
                </Tooltip>
            </div>

            <div className="flex items-center justify-center">
                <GeneralStatsPaperBlocks generalStats={leData?.generalStats} />
            </div>

            <div>
                <Button
                    h="3rem"
                    variant="default"
                    rightSection={<IconArrowRight />}
                    onClick={() => navigate("/explorer")}
                >
                    Explore your games
                </Button>
            </div>

            <div className="flex items-center justify-center">
                <Divider w="42rem" />
            </div>
            <div>
                <div>Openings for</div>
                <SegmentedControl
                    w="12rem"
                    className="mt-0.5"
                    styles={{
                        root: {
                            backgroundColor: "#1c1d21",
                        },
                    }}
                    color="blue"
                    value={playerColor}
                    onChange={(color) => setPlayerColor(color as PlayerColor)}
                    data={[
                        {
                            value: "w",
                            label: "White",
                        },
                        {
                            value: "b",
                            label: "Black",
                        },
                    ]}
                />
            </div>

            <DataTable
                w="100vw"
                maw="900px"
                withTableBorder={false}
                borderRadius="sm"
                // withColumnBorders
                // striped
                highlightOnHover
                verticalSpacing="md"
                backgroundColor="transparent"
                // backgroundColor="inherit"
                // provide data
                records={records}
                // define columns
                columns={[
                    {
                        accessor: "name",
                        // this column has a custom title
                        title: "Opening",
                        // right-align column
                        textAlign: "left",
                        sortable: true,
                        width: isMobile ? "auto" : "250px",
                    },
                    {
                        accessor: "timesPlayed",
                        sortable: true,
                        textAlign: "center",
                        title: width < 600 ? "Plays" : "Times played",
                        render: (element) => <Text c="dimmed">{element.timesPlayed}</Text>,
                    },
                    {
                        accessor: "rating",
                        sortable: true,
                        textAlign: "center",
                        width: "15%",
                        render: (element) => <Text c="dimmed">{element.rating}</Text>,
                    },
                    ...(!isMobile
                        ? ([
                              {
                                  accessor: "averageEval",
                                  sortable: true,
                                  textAlign: "center",
                                  render: (element: any) => <Text c="dimmed">{element.averageEval}</Text>,
                              },
                          ] as any)
                        : []),
                    {
                        accessor: "actions",
                        title: "",
                        textAlign: "right",
                        render: (element) => (
                            <Group gap={4} justify="right" wrap="nowrap">
                                <Button size="sm" variant="outline">
                                    Details
                                </Button>
                                {width > 700 && (
                                    <Button
                                        variant="transparent"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/?position=${element.id}`);
                                        }}
                                    >
                                        Play
                                    </Button>
                                )}{" "}
                            </Group>
                        ),
                    },
                ]}
                // execute this callback when a row is clicked
                onRowClick={({ record: element }) => window.location.assign(`/stats/${element.id}`)}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
            />
        </div>
    );
};

export default Stats;
